/**
 * External dependencies
 */
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
/**
 * Internal dependencies
 */
import { i18ns } from '@naruto/naruto-models/src/core/i18n';
import useTools from '@naruto/naruto-hooks/src/useTools';

dayjs.extend(timezone); // Support Time Zone
dayjs.extend(utc);

export enum TIMEZONE {
  TP = 'Asia/Taipei'
}

export interface useDate {
  rocDay: (
    dayString: string,
    type: 'text' | 'dot',
    prefix?: string,
    postfix?: string,
    translation?: { year: string; month: string; day: string }
  ) => string;
  formatWithTZ: (dayString: string | Date, format?: string) => string;
  withTZ: (dayString: string | Date) => dayjs.Dayjs | undefined;
  toISOString: (dayString: string | Date) => string;
  toDate: (dateString: string) => Date;
  getDays: (startDate: string, endDate: string) => number;
  getToday: () => string;
}

/**
 * 描述 : 取得日期轉換相關功能
 */

export default function useDate(): useDate {
  /* Global & Local State */
  const { timeParser } = useTools();
  /* Main */
  return {
    rocDay: (
      dayString: string,
      type: 'text' | 'dot',
      prefix = '',
      postfix = '',
      translation = { year: '', month: '', day: '' }
    ) => {
      const rocNumberArray: number[] = timeParser.ceToRoc(dayString);
      if (type === 'text') {
        return `${prefix || ''} ${rocNumberArray[0]} ${
          translation.year ? translation.year : i18ns.__('frontend.global.property.rocyear')
        } ${rocNumberArray[1]} ${
          translation.month ? translation.month : i18ns.__('frontend.global.property.rocmonth')
        } ${rocNumberArray[2]} ${translation.day ? translation.day : i18ns.__('frontend.global.property.rocday')} ${
          postfix || ''
        }`;
      }
      if (type === 'dot') {
        return `${prefix || ''}${rocNumberArray[0]}.${rocNumberArray[1]}.${rocNumberArray[2]}${postfix || ''}`;
      }
      return dayString;
    },
    formatWithTZ: (dayString: string | Date, format = 'ddd, MMM DD, YYYY') =>
      dayString ? dayjs(dayString).tz(TIMEZONE.TP).format(format) : '-',
    withTZ: (dayString: string | Date) => (dayString ? dayjs(dayString).tz(TIMEZONE.TP) : undefined),
    toISOString: (dayString: string | Date) => dayjs(dayString).tz(TIMEZONE.TP).toISOString(),
    toDate: (dateString: string) => dayjs(dateString).toDate(),
    getDays: (startDate: string, endDate: string) =>
      dayjs(endDate).startOf('d').diff(dayjs(startDate).startOf('d'), 'd') + 1,
    getToday: () => dayjs().toISOString()
  };
}
